import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { Dictaphone } from '../components/Dictaphone'
import useMediaDevices from '@/hooks/useMediaDevices'
import { DeviceProps } from '@/components/ui/dialogs/DeviceSelectionDialog'
import { useAppSelector } from '@/redux/rootReducer'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { useCreateConsultationDictaphoneMutation } from '@/core/services/consultation'

export type DictaphoneContainerProps = {
  consultationId?: string
}

export function DictaphoneContainer(props: DictaphoneContainerProps) {
  const { consultationId } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const mediaDevices = useMediaDevices()
  const customDevices = useAppSelector(selectCustomDevices)
  const [selectDevice, setSelectDevice] = useState<string>('')

  const [createConsultationDictaphone, { isLoading: isCreatingDictaphone }] = useCreateConsultationDictaphoneMutation()

  const devices = useMemo((): DeviceProps[] => {
    // merge custom devices with media devices
    const x = mediaDevices.map((device) => {
      return {
        id: device.deviceId,
        isCustom: false,
        isSelected: false,
        label: device.label,
        iconURL: '',
        type: device.kind,
      }
    })
    const y = _.map(customDevices, (customDevice) => {
      const device = _.find(mediaDevices, (mediaDevice) => mediaDevice.label === customDevice.device.name)
      return _.merge({}, customDevice, { available: !!device, device })
    }).map((customDevice) => {
      return {
        id: customDevice.device.deviceId,
        isCustom: true,
        isSelected: false,
        label: customDevice.device.name,
        iconURL: customDevice.iconURL,
        type: customDevice.device.type,
      }
    })
    return [...x, ...y]
  }, [mediaDevices, customDevices])

  // handle default dictaphone device
  useEffect(() => {
    const value = localStorage.getItem('DictaphoneDevice')
    if (value) {
      if (mediaDevices.length === 0) return
      // check wether value still 'exists in mediaDevices
      const device = mediaDevices.find((device) => device.deviceId === value)
      if (device) {
        setSelectDevice(value)
      } else {
        localStorage.removeItem('DictaphoneDevice')
      }
    }
  }, [mediaDevices])

  const handleRecord = (file: File) => {
    if (!consultationId) return

    createConsultationDictaphone({ consultationId, file })
      .unwrap()
      .then(() => {
        enqueueSnackbar(t('notification.consultation.dictaphone.success'), { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(t('notification.consultation.dictaphone.error'), { variant: 'error' })
      })
  }

  return <Dictaphone devices={devices} defaultDeviceId={selectDevice} />
}

export default DictaphoneContainer
